import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/hooks/useAuthStore';
import { Button } from '@/components/ui/button';
import { AnimatedPyramidLogo } from '@/components/landing/AnimatedLedgerLoop';
import { motion } from 'framer-motion';
import Footer from '@/components/Footer';
import { Brain, PieChart, ShieldCheck, Eye, FileCheck, Lock, Users } from 'lucide-react';

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 },
  };

  return (
    <div className="min-h-screen bg-capledger-mist">
      {/* ── HERO ── */}
      <section className="min-h-screen flex flex-col items-center justify-center text-center px-6 relative">
        <motion.div
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          className="mb-8"
        >
          <AnimatedPyramidLogo variant="hero" />
        </motion.div>

        <motion.div
          className="max-w-2xl text-capledger-navy"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.6 }}
        >
          <p className="text-lg md:text-xl leading-relaxed mb-3">
            The system of record for equity, valuation, and compliance.
            <br className="hidden sm:block" />
            Built for founders, investors, and regulators.
          </p>
          <p className="text-sm text-muted-foreground mb-10">
            Multi-jurisdiction ready&ensp;•&ensp;Audit-grade records&ensp;•&ensp;AI-powered
            valuation
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            <motion.div whileHover={{ scale: 1.04 }}>
              <Button
                size="lg"
                onClick={() => navigate('/auth?mode=signup')}
                className="px-8 py-6 text-lg font-semibold bg-capledger-navy hover:bg-capledger-navy-dark text-white shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Get Started
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.04 }}>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/auth?mode=signin')}
                className="px-8 py-6 text-lg font-semibold border-2 border-capledger-navy text-capledger-navy hover:bg-capledger-navy hover:text-white transition-all duration-200"
              >
                Sign In
              </Button>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-8"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-capledger-navy rounded-full flex justify-center">
            <div className="w-1 h-3 bg-capledger-navy rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* ── WHAT CAPLEDGER DOES ── */}
      <section className="bg-white py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            {...fadeUp}
            className="text-3xl md:text-4xl font-bold text-capledger-navy text-center mb-14"
          >
            Everything your cap table should have been.
          </motion.h2>

          <div className="grid gap-10 md:grid-cols-3">
            {[
              {
                icon: Brain,
                title: 'Valuation Intelligence',
                lead: 'Know what your company is worth — before someone else tells you.',
                bullets: [
                  'AI-powered valuation ranges',
                  'Scenario modeling and dilution impact',
                  'Benchmarking across markets and stages',
                ],
              },
              {
                icon: PieChart,
                title: 'Equity Control',
                lead: 'Your cap table is no longer a spreadsheet.',
                bullets: [
                  'Real-time ownership tracking',
                  'Automated share register and certificates',
                  'Secondary transfers and option management',
                ],
              },
              {
                icon: ShieldCheck,
                title: 'Compliance & Audit',
                lead: 'Built for scrutiny, not just convenience.',
                bullets: [
                  'Automated filings and governance packs',
                  'Full audit trail of every action',
                  'Regulator and investor-ready reporting',
                ],
              },
            ].map((col, i) => (
              <motion.div
                key={col.title}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 rounded-lg border border-border bg-card hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-capledger-teal/10 flex items-center justify-center mb-4">
                  <col.icon className="h-6 w-6 text-capledger-teal" />
                </div>
                <h3 className="text-lg font-semibold text-capledger-navy mb-2">{col.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{col.lead}</p>
                <ul className="space-y-2">
                  {col.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-capledger-teal shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCT SECTION ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h2
            {...fadeUp}
            className="text-3xl md:text-4xl font-bold text-capledger-navy mb-4"
          >
            Clarity at every stage of your company
          </motion.h2>
          <motion.p {...fadeUp} className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12">
            From your first round to exit, CapLedger keeps your equity structured, traceable, and
            defensible.
          </motion.p>
          <motion.div
            {...fadeUp}
            className="rounded-xl border border-border bg-card p-12 text-muted-foreground text-sm italic"
          >
            Product screenshot placeholder
          </motion.div>
        </div>
      </section>

      {/* ── VALUE / PROBLEM ── */}
      <section className="bg-white py-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <motion.h2
            {...fadeUp}
            className="text-3xl md:text-4xl font-bold text-capledger-navy mb-6"
          >
            Raising capital shouldn't be guesswork.
          </motion.h2>
          <motion.div
            {...fadeUp}
            className="space-y-4 text-muted-foreground text-base leading-relaxed"
          >
            <p>
              Most founders don't know what their company is worth. Most investors won't tell you.
            </p>
            <p>CapLedger gives you the tools to:</p>
            <ul className="space-y-2 pl-1">
              {[
                'understand your valuation before you negotiate',
                'model the impact of every decision',
                'maintain a clean, compliant equity structure',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-capledger-teal shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <p className="font-medium text-capledger-navy">
              So you can raise with confidence — and keep control.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── TRUST ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <motion.h2
            {...fadeUp}
            className="text-3xl md:text-4xl font-bold text-capledger-navy mb-8"
          >
            Designed for real-world accountability
          </motion.h2>
          <motion.div {...fadeUp} className="grid gap-4 sm:grid-cols-2">
            {[
              { icon: Eye, text: 'Every number is traceable' },
              { icon: FileCheck, text: 'Every change is recorded' },
              { icon: Lock, text: 'Every document is verifiable' },
              { icon: Users, text: 'Every stakeholder sees what they should — and nothing more' },
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card"
              >
                <item.icon className="h-5 w-5 text-capledger-teal mt-0.5 shrink-0" />
                <span className="text-sm text-capledger-navy">{item.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TARGET USERS ── */}
      <section className="bg-white py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            {...fadeUp}
            className="flex flex-col md:flex-row justify-center gap-6 md:gap-12 text-center"
          >
            {[
              'For founders raising capital',
              'For investors evaluating deals',
              'For auditors and regulators verifying compliance',
            ].map((t) => (
              <p key={t} className="text-base font-medium text-capledger-navy">
                {t}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-2xl text-center">
          <motion.h2
            {...fadeUp}
            className="text-3xl md:text-4xl font-bold text-capledger-navy mb-4"
          >
            Start with clarity. Scale with confidence.
          </motion.h2>
          <motion.p {...fadeUp} className="text-lg text-muted-foreground mb-8">
            Set up your cap table, run your valuation, and structure your next round — all in one
            place.
          </motion.p>
          <motion.div {...fadeUp}>
            <Button
              size="lg"
              onClick={() => navigate('/auth?mode=signup')}
              className="px-10 py-6 text-lg font-semibold bg-capledger-navy hover:bg-capledger-navy-dark text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Get Started
            </Button>
            <p className="mt-4 text-sm text-muted-foreground">
              Free to start. Upgrade when you grow.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
